import { FetchResult } from 'apollo-boost';
import { compact, get, reduce } from 'lodash';
import { combineEpics, Epic, ofType } from 'redux-observable';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { Action } from '../../actions';
import { changeLocation, showCommonDataFetchError, updateRegions } from '../../actions/common';
import { updateDatesIntervalEditors } from '../../actions/datesIntervalEditor';
import {
    closeEditPageItemNameEditor,
    showEditPageItemNameEditorErrors,
    ShowEditPageItemNameEditorErrorsAction
} from '../../actions/editPageItemNameEditor';
import {
    CANCEL_PRICE_PACKAGE_EDITOR_REGIONS,
    CancelPricePackageEditorRegionsAction,
    GET_PRICE_PACKAGE_DATA_FOR_CREATION,
    GET_PRICE_PACKAGES_FOR_EDIT,
    GetPricePackageDataForCreationAction,
    GetPricePackagesForEditAction,
    SAVE_PRICE_PACKAGE_EDITOR_NAME,
    SAVE_PRICE_PACKAGE_EDITOR_REGIONS,
    SAVE_PRICE_PACKAGES,
    SavePricePackageEditorNameAction,
    SavePricePackageEditorRegionsAction,
    SavePricePackagesAction,
    showCommonPricePackageSaveError,
    showPricePackageEditErrors,
    UPDATE_PRICE_PACKAGE_EDITOR_REGIONS,
    updatePricePackageEditorRegions,
    UpdatePricePackageEditorRegionsAction,
    updatePricePackageName,
    updatePricePackagesDataForEdit
} from '../../actions/pricePackagesEdit';
import { cancelChangesRegionsEditor, closeRegionsEditor } from '../../actions/regionsEditor';
import { ErrorItem } from '../../components/ErrorsPresenter/ErrorsPresenter';
import {
    getPricePackagesValueFromEditors,
    makePricePackageDataForCreation,
    mapPeriodsEditorsDataFromPricePackages,
    mapPricePackageDataToCreateInput,
    mapPricePackageDataToEditInput,
    mapPricePackagesEditData
} from '../../mappers/pricePackages/pricePackages';
import { awaitCommonData } from '../../operators';
import { selectEditPageItemNameEditorValue } from '../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { selectRegionTreeForTransformEditorByType } from '../../selectors/regionsTransformEditor/regionsTransformEditor';
import { ApiMethodExecutingError } from '../../service/Errors';
import { LangUtils } from '../../service/Lang/Lang';
import {
    compactPricePackagesClientsError,
    mapPricePackagesValidationErrors,
    packagePriceErrorMapper
} from '../../service/PricePackagesHelpers/PricePackagesErrorMapper/PricePackagesErrorMapper';
import { PricePackagesHelpers } from '../../service/PricePackagesHelpers/PricePackagesHelpers';
import { RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import { EpicDeps } from '../../service/StoreFactory';
import { UrlHelpers } from '../../service/UrlHelpers';
import { Region } from '../../typings/common';
import { AddPricePackage, GdGeotreeType, UpdatePricePackages } from '../../typings/graphql-grid';
import { PricePackageEditData, PricePackageId } from '../../typings/pricePackages';
import { State } from '../../typings/state';

export const initPackagesEditors = (data: Record<PricePackageId, PricePackageEditData>): Action[] => {
    const periodsEditorsData = mapPeriodsEditorsDataFromPricePackages(data);

    return compact([periodsEditorsData && updateDatesIntervalEditors(periodsEditorsData)]);
};

const cancelPricePackageEditorRegionsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(CANCEL_PRICE_PACKAGE_EDITOR_REGIONS),
        map(
            (action: CancelPricePackageEditorRegionsAction): Action => {
                return cancelChangesRegionsEditor(action.pricePackageId, action.selectedMap);
            }
        )
    );
};

const savePricePackageEditorRegionEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_PRICE_PACKAGE_EDITOR_REGIONS),
        map(
            (action: SavePricePackageEditorRegionsAction): Action => {
                const { id, selectedMap, tree, geoType, isFixed } = action.data;

                return updatePricePackageEditorRegions({ id, selectedMap, tree, geoType, isFixed });
            }
        )
    );
};

const updatePricePackageEditorRegionEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(UPDATE_PRICE_PACKAGE_EDITOR_REGIONS),
        map(
            (action: UpdatePricePackageEditorRegionsAction): Action => {
                return closeRegionsEditor(action.data.id);
            }
        )
    );
};

const getPricePackagesForEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(GET_PRICE_PACKAGES_FOR_EDIT),
        awaitCommonData(action$, store$),
        mergeMap(
            (action: GetPricePackagesForEditAction): Observable<Action> => {
                const state = store$.value;
                const { pricePackagesIds } = state.editPricePackages;

                return forkJoin(
                    deps.pricePackagesDataProvider.fetchPricePackagesDataForEdit(pricePackagesIds),
                    RegionsHelpers.fetchRegions()
                ).pipe(
                    mergeMap(
                        ([pricePackagesData, regionsData]): Observable<Action> => {
                            const regionTree: Region[] = regionsData
                                ? RegionsHelpers.getRegionsTree(
                                      regionsData,
                                      LangUtils.getCurrentLang(),
                                      GdGeotreeType.RUSSIAN
                                  )
                                : [];

                            const { data } = mapPricePackagesEditData(pricePackagesData, regionTree);

                            return of(
                                updatePricePackagesDataForEdit(data),
                                ...initPackagesEditors(data),
                                updateRegions(regionsData)
                            );
                        }
                    )
                );
            }
        ),
        catchError(
            (err: ApiMethodExecutingError): Observable<Action> => {
                return of(showCommonDataFetchError(err));
            }
        )
    );
};

const getPricePackageDataForCreationEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(GET_PRICE_PACKAGE_DATA_FOR_CREATION),
        awaitCommonData(action$, store$),
        mergeMap(
            (action: GetPricePackageDataForCreationAction): Observable<Action> => {
                return from(RegionsHelpers.fetchRegions()).pipe(
                    mergeMap(
                        (regionsData): Observable<Action> => {
                            const pricePackagesData = makePricePackageDataForCreation();

                            return of(
                                updatePricePackagesDataForEdit(pricePackagesData),
                                ...initPackagesEditors(pricePackagesData),
                                updateRegions(regionsData)
                            );
                        }
                    )
                );
            }
        ),
        catchError(
            (err: ApiMethodExecutingError): Observable<Action> => {
                return of(showCommonDataFetchError(err));
            }
        )
    );
};

const updatePricePackageNameEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_PRICE_PACKAGE_EDITOR_NAME),
        switchMap((action: SavePricePackageEditorNameAction): Action[] => {
            const { pricePackageId } = action;
            const state = store$.value;
            const { editPageItemNameEditor } = state;
            const editorId = PricePackagesHelpers.getPricePackageEditPageItemEditorId(pricePackageId);
            const editorData = selectEditPageItemNameEditorValue(editPageItemNameEditor, editorId);

            return [updatePricePackageName(pricePackageId, editorData.value), closeEditPageItemNameEditor(editorId)];
        })
    );
};

const savePricePackagesEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_PRICE_PACKAGES),
        mergeMap(
            (action: SavePricePackagesAction): Observable<Action> => {
                const state = store$.value;
                const { login } = state.commonData;
                const {
                    editPricePackages: { isNew, pricePackages, pricePackagesIds }
                } = state;
                const regionTree = selectRegionTreeForTransformEditorByType(state, GdGeotreeType.RUSSIAN);

                const pricePackagesData = getPricePackagesValueFromEditors(
                    pricePackages,
                    state.regionsEditor,
                    state.editPageItemNameEditor,
                    state.datesIntervalEditor,
                    regionTree
                );
                let request$;

                if (isNew) {
                    request$ = deps.pricePackagesMutationExecutor.createPricePackage(
                        mapPricePackageDataToCreateInput(pricePackagesData),
                        login
                    );
                } else {
                    request$ = deps.pricePackagesMutationExecutor.updatePricePackagesData(
                        mapPricePackageDataToEditInput(pricePackagesData),
                        login
                    );
                }

                return forkJoin(request$).pipe(
                    mergeMap((response: FetchResult<UpdatePricePackages> | FetchResult<AddPricePackage>): Action[] => {
                        const updateErrors = get(response[0], 'data.updatePricePackages.validationResult.errors', []);
                        const addErrors = get(response[0], 'data.addPricePackages.validationResult.errors', []);

                        if (updateErrors.length || addErrors.length) {
                            const clientsIds = Object.keys(pricePackages).map(key => {
                                return pricePackages[key].clientIds || [];
                            });
                            const compactErrors = updateErrors.length
                                ? compactPricePackagesClientsError(updateErrors, clientsIds)
                                : addErrors;
                            const errors = mapPricePackagesValidationErrors(compactErrors, pricePackagesIds);
                            const titleErrorsAction = reduce(
                                errors,
                                (
                                    acc: ShowEditPageItemNameEditorErrorsAction[],
                                    value: Record<string, ErrorItem>,
                                    key: string
                                ): ShowEditPageItemNameEditorErrorsAction[] => {
                                    if (value.title) {
                                        acc.push(showEditPageItemNameEditorErrors(`package-${key}`, [value.title]));
                                    }
                                    return acc;
                                },
                                []
                            );

                            return [...titleErrorsAction, showPricePackageEditErrors(errors)];
                        }

                        return [changeLocation(UrlHelpers.getPricePackagesGridUrl())];
                    }),
                    catchError(
                        (err: ApiMethodExecutingError): Observable<Action> => {
                            return of(
                                showCommonPricePackageSaveError([
                                    {
                                        text: packagePriceErrorMapper({
                                            code: 'API_ERROR',
                                            params: '',
                                            path: 'commonPackagesErrors'
                                        })
                                    }
                                ])
                            );
                        }
                    )
                );
            }
        )
    );
};

export const pricePackagesEditEpic = combineEpics(
    cancelPricePackageEditorRegionsEpic,
    savePricePackageEditorRegionEpic,
    updatePricePackageEditorRegionEpic,
    getPricePackagesForEditEpic,
    getPricePackageDataForCreationEpic,
    updatePricePackageNameEpic,
    savePricePackagesEpic
);
